/**
 * Tom Select wrapper replacing select2 (`$_select` / `$_select2`).
 *
 * Parity with the old select2 setup:
 *  - remote options from `/…/options?term=` returning `{ results: [{ id, text }] }`
 *  - fresh request every time the dropdown opens, 600ms typing delay, minimumInputLength
 *  - allow clear, raw HTML labels (escapeMarkup was identity), Indonesian messages
 *  - Enter never submits the form, Tab selects the highlighted option
 *  - the callback runs on user selection with `this` = the original <select>, after its value updated
 *  - external `<option>` edits on the original <select> (append/empty) are mirrored automatically
 */
import TomSelect from 'tom-select';

const MESSAGES = {
    noResults: 'Tidak ada data yang sesuai',
    searching: 'Mencari…',
    errorLoading: 'Data tidak boleh diambil.',
    inputTooShort: (n) => `Masukkan ${n} huruf lagi`,
};

/** Select classes the wrapper may keep: Bootstrap sizing/validation/width, which style it. */
const WRAPPER_CLASSES = /^(form-control(-sm|-lg)?|form-select(-sm|-lg)?|is-(in)?valid|w-100)$/;

function optionSignature(select) {
    const opts = Array.from(select.options)
        .map((o) => `${o.value}\u0001${o.text}`)
        .sort()
        .join('\u0002');
    return `${opts}\u0003${Array.from(select.selectedOptions).map((o) => o.value).join(',')}`;
}

function resolveWidth(select) {
    if (select.dataset.width) return select.dataset.width;
    if (select.style.width) return select.style.width;
    if (select.classList.contains('w-100')) return '100%';
    return null;
}

function buildUrl(url, query) {
    const params = new URLSearchParams({ term: query, _type: 'query' });
    return url + (url.includes('?') ? '&' : '?') + params.toString();
}

/**
 * Rebuild Tom Select's options/value from the original <select>.
 * (TomSelect#sync() re-reads with the default `value` field and would drop our `id`-keyed options.)
 */
export function syncFromDom(select) {
    const ts = select.tomselect;
    if (!ts) return;
    // Drop Tom Select's own blank placeholder first, so the browser's rule applies: a single
    // select with no chosen option shows its first one (an <option> appended by page code).
    select.querySelectorAll('option[data-ts-placeholder]').forEach((o) => o.remove());
    // Read the DOM first: clear() below rewrites the original select's selection.
    // $option links each entry to its element; without it Tom Select appends a duplicate <option>.
    const options = Array.from(select.options).map((o) => ({ id: o.value, text: o.text, $option: o }));
    const selected = Array.from(select.selectedOptions).map((o) => o.value);
    ts.clear(true);
    ts.clearOptions(() => false);
    ts.addOptions(options);
    ts.setValue(select.multiple ? selected : selected[0] ?? '', true);
    if (select.disabled) ts.disable();
    else ts.enable();
    ts.lastQuery = null;
}

/**
 * With nothing selected, Tom Select appends a selected `<option value="">` to the original select.
 * select2 added nothing, so options appended later became the value natively. Tag those
 * placeholders (not the page's own blank options) so syncFromDom can drop them.
 */
function tagPlaceholders(select, ts, ownOptions) {
    const tag = (before) => {
        for (const o of select.options) {
            if (o.value === '' && !before.has(o)) o.dataset.tsPlaceholder = '';
        }
    };
    tag(ownOptions); // the one added while Tom Select set itself up
    const update = ts.updateOriginalInput.bind(ts);
    ts.updateOriginalInput = (opts) => {
        const before = new Set(select.options);
        update(opts);
        tag(before);
    };
}

function mirrorOriginalSelect(select, ts) {
    let last = optionSignature(select);
    const observer = new MutationObserver(() => {
        const sig = optionSignature(select);
        if (sig === last) return;
        syncFromDom(select);
        last = optionSignature(select);
    });
    observer.observe(select, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['selected', 'value', 'disabled'] });
    ts.on('change', () => {
        last = optionSignature(select);
    });
    return observer;
}

/**
 * Enhance one <select>. Returns the TomSelect instance (also at `select.tomselect`).
 * Options: { url, onSelect, minimum }
 */
export function createSelect(select, { url, onSelect, minimum = 0 } = {}) {
    if (select.tomselect) return select.tomselect;

    const settings = {
        valueField: 'id',
        labelField: 'text',
        searchField: ['text'],
        placeholder: select.getAttribute('placeholder') || '',
        allowEmptyOption: true,
        maxOptions: null,
        selectOnTab: true,
        loadThrottle: 600,
        dropdownParent: 'body',
        plugins: select.multiple ? ['remove_button', 'clear_button'] : ['clear_button'],
        render: {
            // Some option endpoints return text: null; select2 showed those as blank rows.
            option: (d) => `<div>${d.text ?? ''}</div>`,
            item: (d) => `<div>${d.text ?? ''}</div>`,
            no_results: () => `<div class="no-results">${MESSAGES.noResults}</div>`,
            loading: () => `<div class="spinner-border spinner-border-sm text-secondary m-2" role="status"><span class="visually-hidden">${MESSAGES.searching}</span></div>`,
        },
    };

    if (url) {
        let activeQuery = null;
        Object.assign(settings, {
            preload: false,
            // The server already filtered; don't let Tom Select hide results whose text doesn't contain the term.
            score: () => () => 1,
            shouldLoad: (query) => query.length >= minimum,
            load(query, callback) {
                activeQuery = query;
                fetch(buildUrl(url, query), {
                    credentials: 'same-origin',
                    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
                })
                    .then((r) => (r.ok ? r.json() : Promise.reject(r)))
                    .then((json) => {
                        if (activeQuery !== query) return;
                        this.clearOptions();
                        callback((json && json.results) || []);
                        this.positionDropdown();
                    })
                    .catch(() => {
                        if (activeQuery !== query) return;
                        this.clearOptions();
                        callback();
                        const el = this.dropdown_content;
                        if (el) el.innerHTML = `<div class="no-results">${MESSAGES.errorLoading}</div>`;
                        this.positionDropdown();
                    });
            },
        });
        if (minimum > 0) {
            settings.render.not_loading = (data) =>
                data.input.length < minimum ? `<div class="no-results">${MESSAGES.inputTooShort(minimum - data.input.length)}</div>` : '';
        }
    }

    const ownOptions = new Set(select.options);
    const ts = new TomSelect(select, settings);
    tagPlaceholders(select, ts, ownOptions);

    // Tom Select copies the select's classes onto its wrapper <div>; select2 did not. Page code
    // looks selects up by class (qsa('.technician_id') then App.val on each), so a copied class
    // made the wrapper match too and read as an empty value. Keep only Bootstrap styling classes.
    select.classList.forEach((c) => {
        if (!WRAPPER_CLASSES.test(c)) ts.wrapper.classList.remove(c);
    });

    const width = resolveWidth(select);
    if (width) ts.wrapper.style.width = width;
    if (select.classList.contains('form-control-sm')) ts.wrapper.classList.add('form-control-sm');

    if (url) {
        // select2 queried the server every time it opened; mirror that.
        ts.on('dropdown_open', () => {
            ts.loadedSearches = {};
            const query = ts.inputValue();
            if (query.length >= minimum) {
                ts.clearOptions();
                ts.load(query);
            }
        });
    }

    // Enter must never submit the surrounding form (select2 swallowed it too).
    ts.control_input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !ts.isOpen) {
            e.preventDefault();
            ts.open();
        }
    });

    const onScrollOrResize = () => {
        if (ts.isOpen) ts.positionDropdown();
    };
    window.addEventListener('scroll', onScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    ts.on('destroy', () => {
        window.removeEventListener('scroll', onScrollOrResize, { capture: true });
        window.removeEventListener('resize', onScrollOrResize);
    });

    ts.on('item_add', () => {
        select._selectPending = true;
    });
    setSelectCallback(select, onSelect);

    select._selectObserver = mirrorOriginalSelect(select, ts);
    return ts;
}

/**
 * Attach (or replace) the selection callback. item_add also fires for silent updates, which
 * never emit `change`; so only a change that carries a value counts as a selection
 * (clearing is not one, as in select2).
 */
export function setSelectCallback(select, onSelect) {
    if (select._selectChangeHandler) select.removeEventListener('change', select._selectChangeHandler);
    select._selectChangeHandler = null;
    if (!onSelect) return;
    select._selectChangeHandler = (e) => {
        const fire = select._selectPending && select.value !== '';
        select._selectPending = false;
        if (fire) onSelect.call(select, e);
    };
    select.addEventListener('change', select._selectChangeHandler);
}

/** `$(select).off()` on a select2 element: detach its selection callback, keep the widget. */
export function offSelect(target) {
    const els = typeof target === 'string' ? document.querySelectorAll(target) : target instanceof Element ? [target] : target;
    Array.from(els || []).forEach((el) => setSelectCallback(el, null));
}

/**
 * `$_select(selector, url, cb, minimum)`: enhance every matching <select>.
 * Calling again with the same URL is a no-op (rows re-run init after each append);
 * calling with a different URL rebuilds the widget, like re-running select2 did
 * for dependent selects (province -> city).
 */
export function initSelect(target, url, onSelect, minimum) {
    const els = typeof target === 'string' ? document.querySelectorAll(target) : target instanceof Element ? [target] : target;
    Array.from(els || []).forEach((el) => {
        if (el.tomselect && el._selectUrl !== (url ?? null)) destroySelect(el);
        if (el.tomselect) {
            // Same URL: keep the widget; re-attach a callback only if it was detached (offSelect).
            if (onSelect && !el._selectChangeHandler) setSelectCallback(el, onSelect);
            return;
        }
        createSelect(el, { url, onSelect, minimum: minimum ?? 0 });
        el._selectUrl = url ?? null;
    });
}

/** Set the value of an enhanced (or plain) select, firing change like `.val(v).trigger('change')`. */
export function setSelectValue(target, value, silent = false) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;
    if (el.tomselect) el.tomselect.setValue(value, silent);
    else {
        el.value = value;
        if (!silent) el.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

/** Replace a select's options with [{id, text}] and optionally select one. */
export function setSelectOptions(target, options, selected) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;
    if (el.tomselect) {
        const ts = el.tomselect;
        ts.clear(true);
        ts.clearOptions();
        ts.addOptions(options);
        if (selected != null) ts.setValue(selected, true);
        return;
    }
    el.innerHTML = options.map((o) => `<option value="${o.id}">${o.text}</option>`).join('');
    if (selected != null) el.value = selected;
}

export function destroySelect(target) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el || !el.tomselect) return;
    el._selectObserver?.disconnect();
    el._selectObserver = null;
    if (el._selectChangeHandler) el.removeEventListener('change', el._selectChangeHandler);
    el._selectChangeHandler = null;
    el.tomselect.destroy();
    delete el._selectUrl;
}
